import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import ResponsiveModalAbsolute from "../../../components/ResponsiveModalAbsolute";
import useHeaders from "../../../hooks/useHeaders";
import { useAppSelector } from "../../../hooks/hooks";
import { Limpieza } from "./LimpiezasHotel";

interface Props {
    limpieza: Limpieza;
    modalVisible: boolean;
    setModalVisible: (b: boolean) => void;
    refresh: () => void;
}

export const ModalGestionarLimpieza = ({
    limpieza,
    modalVisible,
    setModalVisible,
    refresh,
}: Props) => {
    const { headers } = useHeaders();
    const { id } = useAppSelector((state) => state.usuario_logeado); // ID del usuario actual
    const [notas, setNotas] = useState(limpieza.notas || "");
    const [loading, setLoading] = useState(false);

    const handleAccion = async (accion: "INICIAR" | "TERMINAR") => {
        setLoading(true);
        try {
            await axios.put(
                `${process.env.REACT_APP_PROXY}/limpieza_habitaciones/gestionar/${limpieza.id}`,
                {
                    accion,
                    id_usuario: id,
                    notas: accion === "TERMINAR" ? notas : null,
                },
                { headers },
            );

            Swal.fire(
                "Éxito",
                `Limpieza ${accion === "INICIAR" ? "iniciada" : "finalizada"}`,
                "success",
            );
            refresh();
            setModalVisible(false);
        } catch (error) {
            Swal.fire("Error", "No se pudo actualizar el estado", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <ResponsiveModalAbsolute
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
        >
            <div className="flex flex-col items-center max-w-lg gap-4 p-4 mx-auto md:p-10 text-center">
                <h2 className="text-xl font-bold">Gestionar Limpieza</h2>
                <p className="text-gray-600">
                    Habitación:{" "}
                    <span className="font-bold text-black">
                        {limpieza.habitacion.nombre}
                    </span>
                </p>

                <div className="w-full p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm">
                        Estado actual:
                        <span
                            className={`ml-2 font-bold ${limpieza.estado === "Pendiente" ? "text-red-500" : "text-yellow-500"}`}
                        >
                            {limpieza.estado}
                        </span>
                    </p>
                </div>

                {limpieza.estado === "Pendiente" && (
                    <div className="w-full">
                        <p className="mb-4 text-sm text-gray-500">
                            Haz clic para registrar tu hora de entrada a la
                            habitación.
                        </p>
                        <button
                            disabled={loading}
                            onClick={() => handleAccion("INICIAR")}
                            className="w-full p-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700"
                        >
                            {loading ? "Procesando..." : " Iniciar Limpieza"}
                        </button>
                    </div>
                )}

                {limpieza.estado === "En Proceso" && (
                    <div className="w-full space-y-4">
                        <div className="text-left">
                            <label className="text-sm font-semibold">
                                Notas / Observaciones (Opcional)
                            </label>
                            <textarea
                                value={notas}
                                onChange={(e) => setNotas(e.target.value)}
                                placeholder="Ej: Faltan toallas, se reportó daño en control remoto..."
                                className="w-full mt-1 p-2 border rounded-lg h-24 outline-none focus:border-blue-500"
                            />
                        </div>
                        <button
                            disabled={loading}
                            onClick={() => handleAccion("TERMINAR")}
                            className="w-full p-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700"
                        >
                            {loading
                                ? "Procesando..."
                                : "✔ Finalizar y Liberar Habitación"}
                        </button>
                    </div>
                )}

                {limpieza.estado === "Terminado" && (
                    <div className="text-green-600 font-medium">
                        Esta limpieza ya ha sido completada.
                        <p className="text-gray-500 text-xs mt-2 italic">
                            "{limpieza.notas}"
                        </p>
                    </div>
                )}

                <button
                    onClick={() => setModalVisible(false)}
                    className="mt-2 text-gray-400 hover:text-gray-600 text-sm"
                >
                    Cerrar
                </button>
            </div>
        </ResponsiveModalAbsolute>
    );
};
